import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Text, Icon, Button, SelectInput, TextInput, Status} from '@/ui-kits';
import {NA} from '@/constants';
import {appendPlural, cn} from '@/utils/common_functions';
import {DataTableColumn, SelectInputitem, EditCategoryRequestPayload, CategoryItem, GetCategoryListRequestPayload} from '@/interface';
import {DataTable, AddCategoryModal} from '@/components';
import {
  categoryDataSelector,
  categoryTotalPagesSelector,
  categoryTotalResultSelector,
  patientAdminSuccessSelector,
  patientAdminFailureSelector,
  patientAdminStatus,
  allAppSpecializations,
  categoryCurrentPageSelector,
} from '@/services/redux/selectors';
import {
  getCategoryListRequest,
  addCategoryRequest,
  editCategoryRequest,
  resetPatientAdminMessage,
  getSpecializationRequest,
} from '@/services/redux/slices';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {useToast} from '@/hooks';

export const CategoryManagement: React.FC = () => {
  // ==============================
  // hooks
  // ==============================
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ==============================
  // selectors
  // ==============================
  const categoryData = useSelector(categoryDataSelector);
  const categoryTotalPages = useSelector(categoryTotalPagesSelector);
  const categoryTotalResult = useSelector(categoryTotalResultSelector);
  const categorySuccess = useSelector(patientAdminSuccessSelector) as SuccessCodes;
  const categoryFailure = useSelector(patientAdminFailureSelector) as ErrorCodes;
  const isLoading = useSelector(patientAdminStatus);
  const categoryCurrentPage = useSelector(categoryCurrentPageSelector);
  const categoryOptions = useSelector(allAppSpecializations);

  // ==============================
  // states
  // ==============================
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<SelectInputitem | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [page, setPage] = useState(1);
  const [editingRow, setEditingRow] = useState<EditCategoryRequestPayload | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);

  // ==============================
  // functions
  // ==============================
  function resetFilter() {
    setSelectedCategoryFilter(null);
    setIsFilterActive(false);
    setPage(1);
  }

  const fetchCategories = useCallback(() => {
    let isFilterApplied = false;
    setErrorMessage('');
    const payload: GetCategoryListRequestPayload = {
      page,
    };

    if (selectedCategoryFilter?.id) {
      isFilterApplied = true;
      payload.category = selectedCategoryFilter?.id;
    }

    if (isFilterApplied) {
      setIsFilterActive(true);
    } else {
      setIsFilterActive(false);
    }
    dispatch(getCategoryListRequest(payload));
  }, [selectedCategoryFilter, page, dispatch]);


  const handleSaveNewCategory = (formData: {name: string}) => {
    const capitalizedName = formData.name.charAt(0).toUpperCase() + formData.name.slice(1);
    dispatch(addCategoryRequest({name: capitalizedName}));
    setIsAddCategoryModalOpen(false);
  };

  const handleEditRow = (row: CategoryItem) => {
    setEditingRow({
      id: row.id,
      name: row.name,
      order: row.order,
    });
  };

  const handleSaveRow = (row: EditCategoryRequestPayload) => {
    const capitalizedName = row.name.charAt(0).toUpperCase() + row.name.slice(1);
    dispatch(editCategoryRequest({...row, name: capitalizedName}));
  };

  const handleCancelRow = () => {
    setEditingRow(null);
  };

  const handleEditRowFields = (rowId: number, field: string, value: any) => {
    setEditingRow(p => {
      if (!p) return null;
      if (rowId !== p.id) return null;
      return {
        ...p,
        [field]: value,
      };
    });
  };

  // ==============================
  // data
  // ==============================
  const columns: DataTableColumn<CategoryItem>[] = useMemo((): DataTableColumn<CategoryItem>[] => {
    return [
      {
        name: 'name',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            Category Name
          </Text>
        ),
        render: row => (
          <Text variant="body" className="!font-FigtreeSemiBold truncate text-sm text-ui_input_border_dark">
            {row.name ?? NA}
          </Text>
        ),
        align: 'left',
        width: {minWidth: '150px', maxWidth: '300px'},
      },
      {
        name: 'assignedSpecialists',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            Assigned Specialists
          </Text>
        ),
        render: row => (
          <Text variant="body" className="!font-FigtreeSemiBold text-sm text-ui_input_border_dark">
            {row.specialist_count} {appendPlural('specialist', row.specialist_count)}
          </Text>
        ),
        align: 'left',
        width: {minWidth: '240px', maxWidth: '200px'},
      },
      {
        name: 'visibility',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            Visibility
          </Text>
        ),
        render: row => <Status status={row.specialist_count > 0} variant='visiblity' />,
        align: 'center',
        width: {minWidth: '100px', maxWidth: '150px'},
      },
      {
        name: 'order',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            Order
          </Text>
        ),
        render: row =>
          editingRow?.id === row.id ? (
            <div className="flex justify-center w-full">
              <TextInput
                type="numeric"
                value={String(editingRow.order)}
                onChangeText={value => handleEditRowFields(row.id, 'order', parseInt(value) || 0)}
                wrapperClassName="border-[1px] w-full max-w-[80px] py-0 rounded"
                className={cn('text-sm')}
                inputClassName="py-1 !font-FigtreeRegular text-sm text-center"
                name="order"
              />
            </div>
          ) : (
            <Text variant="body" className="!font-FigtreeSemiBold text-sm text-ui_input_border_dark">
              {row.order}
            </Text>
          ),
        align: 'center',
        width: {minWidth: '80px', maxWidth: '120px'},
      },
      {
        name: 'action',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            Actions
          </Text>
        ),
        render: row =>
          editingRow?.id === row.id ? (
            <div className="flex justify-center w-full gap-4">
              <button className="flex justify-center" onClick={() => handleSaveRow(editingRow)}>
                <Icon type="green_tick_btn" className="size-6" />
              </button>
              <button className="flex justify-center" onClick={handleCancelRow}>
                <Icon type="cross_btn_red" className="size-6" />
              </button>
            </div>
          ) : (
            <div className="flex justify-center w-full gap-4">
              <button className="flex justify-center" onClick={() => handleEditRow(row)}>
                <Icon type="edit_input" className="size-4 self-center" />
              </button>
            </div>
          ),
        align: 'center',
        width: {minWidth: '70px', maxWidth: '100px'},
      },
    ];
  }, [editingRow]);

  // ==============================
  // side effects
  // ==============================
  useEffect(() => {
    dispatch(getSpecializationRequest());
  }, [dispatch]);

  useEffect(() => {
    const timeout = setTimeout(fetchCategories, 500);
    return () => clearTimeout(timeout);
  }, [fetchCategories]);

  useEffect(() => {
    // if category success, fetch categories
    if (categorySuccess) {
      if (['S-10008', 'S-10019', 'S-10011', 'S-10072'].includes(categorySuccess)) {
        showToast(getSuccessMessage(categorySuccess), 'success');
        fetchCategories();
      }
      if (categorySuccess === 'S-10008') {
        setIsAddCategoryModalOpen(false);
      } 
      if (categorySuccess === 'S-10071') {
        showToast(getSuccessMessage(categorySuccess), 'success');
      }
      if (categorySuccess === 'S-10019' || categorySuccess === 'S-10072') {
        setEditingRow(null);
      }
    }
    if (categoryFailure) {
      if (['E-10044', 'E-10054'].includes(categoryFailure)) {
        setErrorMessage(getErrorMessage(categoryFailure));
      } else {
        showToast(getErrorMessage(categoryFailure), 'failure');
      }
    }
    return () => {
      dispatch(resetPatientAdminMessage());
    };
  }, [categorySuccess, categoryFailure, dispatch, fetchCategories, showToast]);

  return (
    <div className="flex flex-col gap-4 p-2 sm:p-4 md:p-6 lg:p-8">
      <div className="flex flex-row justify-between items-start gap-4">
        <div>
          <Text variant="heading" className="mb-1 text-ui_blue">
            Category Management
          </Text>
          <Text variant="title" className="text-ui_input_border_dark !font-FigtreeRegular">
            Manage specialist categories shown to patients.
          </Text>
        </div>
        <Button title="+ Add Category" variant="primary" onClick={() => setIsAddCategoryModalOpen(true)} className="hidden min-[594px]:block" />
      </div>

      <div className="grid grid-cols-2 md:flex md:flex-row gap-4">
        <SelectInput
          value={selectedCategoryFilter?.id || ''}
          onChange={setSelectedCategoryFilter}
          placeholder="All Categories"
          options={categoryOptions}
          className="md:max-w-[200px]"
          wrapperClassName="border"
        />

        <Button title="+ Add Category" variant="primary" onClick={() => setIsAddCategoryModalOpen(true)} className="min-[594px]:hidden" />
        {isFilterActive && <Button title="Reset Filters" variant="secondary" onClick={resetFilter} />}
      </div>

      <DataTable
        columns={columns}
        data={categoryData}
        totalPages={categoryTotalPages}
        currentPage={categoryCurrentPage}
        totalResult={categoryTotalResult}
        onPageChange={setPage}
        errorMessage={errorMessage}
        loading={isLoading}
      />

      {isAddCategoryModalOpen && <AddCategoryModal
        open={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
        onSave={handleSaveNewCategory}
      />}
    </div>
  );
};
